import { TableShape } from '../types/floorPlan.model'

export interface SeatPos {
  x: number
  y: number
}

export const SEAT_RADIUS = 9
export const SEAT_GAP = 6
export const SEAT_HALO_EXTRA = 4
export const SEAT_OUTER_RADIUS = SEAT_RADIUS + SEAT_HALO_EXTRA // halo radius
export const SEAT_MIN_GAP = 1.5 // px kept between neighbouring halos

// Slack subtracted from each table's collision footprint so tables can sit
// close together, only a real overlap of the chairs is blocked, light brushing
// is allowed.
export const COLLISION_SLACK = 30

/**
 * Returns seat positions in the table's LOCAL coordinate space (origin at the
 * table center). Because seats live inside the table's Konva Group, they
 * automatically follow drag / rotation / resize of the table.
 */
export function getSeatPositions(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  if (seatCount <= 0) return []

  if (shape === 'round') {
    return getRoundSeatPositions(seatCount, width, height)
  }

  return getRectSeatPositions(seatCount, width, height)
}

function getRoundSeatPositions(
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  const radius = Math.max(width, height) / 2 + SEAT_GAP + SEAT_RADIUS
  const positions: SeatPos[] = []

  for (let i = 0; i < seatCount; i += 1) {
    // start at the top (−90°) and go clockwise
    const angle = (2 * Math.PI * i) / seatCount - Math.PI / 2
    positions.push({
      x: radius * Math.cos(angle),
      y: radius * Math.sin(angle)
    })
  }

  return positions
}

function getRectSeatPositions(
  seatCount: number,
  width: number,
  height: number
): SeatPos[] {
  const hw = width / 2
  const hh = height / 2
  const offset = SEAT_GAP + SEAT_RADIUS

  // Distribute seats across the 4 sides proportionally to side length.
  // top/bottom weighted by width, left/right by height.
  const weights = [width, width, height, height] // top, bottom, left, right
  const perSide = distributeByWeight(seatCount, weights)
  const [top, bottom, left, right] = perSide

  const positions: SeatPos[] = []

  // top: y fixed above the table, x spread across the width
  spreadAlong(top).forEach(f => {
    positions.push({ x: -hw + f * width, y: -hh - offset })
  })
  // bottom
  spreadAlong(bottom).forEach(f => {
    positions.push({ x: -hw + f * width, y: hh + offset })
  })
  // left: x fixed to the left, y spread across the height
  spreadAlong(left).forEach(f => {
    positions.push({ x: -hw - offset, y: -hh + f * height })
  })
  // right
  spreadAlong(right).forEach(f => {
    positions.push({ x: hw + offset, y: -hh + f * height })
  })

  return positions
}

/**
 * Splits `total` into `weights.length` integer buckets proportionally to the
 * provided weights, using the largest-remainder method so the buckets always
 * sum exactly to `total`.
 */
function distributeByWeight(total: number, weights: number[]): number[] {
  const weightSum = weights.reduce((acc, w) => acc + w, 0)
  if (weightSum === 0) return weights.map(() => 0)

  const raw = weights.map(w => (total * w) / weightSum)
  const floors = raw.map(Math.floor)
  let remainder = total - floors.reduce((acc, n) => acc + n, 0)

  const order = raw
    .map((value, index) => ({ index, frac: value - Math.floor(value) }))
    .sort((a, b) => b.frac - a.frac)

  for (let i = 0; i < order.length && remainder > 0; i += 1) {
    floors[order[i].index] += 1
    remainder -= 1
  }

  return floors
}

/**
 * Returns `n` evenly spaced fractions in (0, 1), inset from both ends so seats
 * don't sit exactly on the table corners: (k + 1) / (n + 1).
 */
function spreadAlong(n: number): number[] {
  const fractions: number[] = []
  for (let k = 0; k < n; k += 1) {
    fractions.push((k + 1) / (n + 1))
  }
  return fractions
}

/** Smallest distance between any two seats. */
function minSeatDistance(seats: SeatPos[]): number {
  let min = Infinity
  for (let i = 0; i < seats.length; i += 1) {
    for (let j = i + 1; j < seats.length; j += 1) {
      const dist = Math.hypot(seats[i].x - seats[j].x, seats[i].y - seats[j].y)
      if (dist < min) min = dist
    }
  }
  return min
}

/** Shrink factor so neighbouring seat halos never overlap (mirrors SeatRing). */
export function getSeatScale(seats: SeatPos[]): number {
  if (seats.length < 2) return 1
  const allowed = minSeatDistance(seats) / 2 - SEAT_MIN_GAP
  return Math.min(1, Math.max(0.15, allowed / SEAT_OUTER_RADIUS))
}

/**
 * Full footprint (table body + seat ring incl. halos) as an axis-aligned box
 * centred on the table. Used for collision so the chairs of neighbouring tables
 * don't overlap. Rotation is ignored, matching the body-box collision model.
 */
export function getTableFootprint(
  shape: TableShape,
  seatCount: number,
  width: number,
  height: number
): { width: number; height: number } {
  const seats = getSeatPositions(shape, seatCount, width, height)
  if (seats.length === 0) return { width, height }

  const outer = SEAT_OUTER_RADIUS * getSeatScale(seats)
  let halfWidth = width / 2
  let halfHeight = height / 2
  seats.forEach(seat => {
    halfWidth = Math.max(halfWidth, Math.abs(seat.x) + outer)
    halfHeight = Math.max(halfHeight, Math.abs(seat.y) + outer)
  })
  return { width: halfWidth * 2, height: halfHeight * 2 }
}

/**
 * Axis-aligned bounding box of a footprint rotated by `rotationDeg`, so collision
 * accounts for a table's orientation (chairs swing out when it's turned).
 */
export function rotatedFootprintAABB(
  footprint: { width: number; height: number },
  rotationDeg: number
): { width: number; height: number } {
  const rad = (rotationDeg * Math.PI) / 180
  const cos = Math.abs(Math.cos(rad))
  const sin = Math.abs(Math.sin(rad))
  return {
    width: footprint.width * cos + footprint.height * sin,
    height: footprint.width * sin + footprint.height * cos
  }
}
